/**
 * Which jobs may receive a lease-to-lease equipment transfer.
 *
 * This mirrors a SERVER rule, and mirroring it is the whole point.
 * `POST /equipment/:id/transfer-to-job` refuses anything else:
 *
 *     const TRANSFERABLE_DESTINATION_STATUSES = ['scheduled', 'active', 'on_hold'];
 *     if (!TRANSFERABLE_DESTINATION_STATUSES.includes(destStatus)) {
 *       return c.json({ error: 'Destination job must be scheduled, active or on hold' }, 400);
 *     }
 *
 * A picker WIDER than that rule offers a job the API will 400 — the dispatcher
 * picks it, gets a red toast, and has no way to tell which jobs are eligible. A
 * picker NARROWER hides a destination the API would have accepted. Either way
 * the two answers disagree, which is the same failure the available-equipment
 * handler avoids by computing `out_of_cert` with the function the write path
 * uses rather than re-deriving it.
 *
 * ─── `on_hold` ADDED, AND THIS IS THE CHANGE THIS FILE WAS WAITING FOR ───────
 *
 * The previous revision of this comment said `on_hold` was "about to change"
 * and named the case: J-4303 left two assets at Ensign 418, and J-4305 — the
 * next job on that same rig — is `on_hold`, so the transfer this endpoint
 * exists for was refused and the only recorded alternative was a yard visit
 * that never happened. Both halves have now moved together, in one change:
 * the server constant quoted above, and this one.
 *
 * A held job is work that has not happened yet, not work that is over.
 *
 * WHY THIS IS NOT THE SAME LIST AS `GET /employee/my-equipment`'s arm 1, which
 * also gained `on_hold`: that one includes `pending` and this one must not.
 * They answer different questions — "is gear on this job still somebody's
 * problem" versus "may gear be moved ONTO this job" — and `pending` is a job
 * with no date agreed, which is a destination nobody should be able to pick.
 * The two coincide on three values today and are deliberately kept apart so
 * they stay free to diverge. The server says the same thing at its own site.
 */

/** Job statuses `POST /equipment/:id/transfer-to-job` accepts as a destination. */
export const TRANSFER_DESTINATION_STATUSES = ['scheduled', 'active', 'on_hold'] as const;

/**
 * May this job receive a transfer?
 *
 * Tolerates both casings: `GET /jobs` answers with raw `SELECT j.*` rows in
 * snake_case, while other job payloads on this client are camelCase.
 */
export function isTransferDestination(job: { status?: string | null }): boolean {
  return (TRANSFER_DESTINATION_STATUSES as readonly string[]).includes(job?.status ?? '');
}
